<template>
    <div class="container">
      <NavigationBar/>
      <div class="mt-5 references-container">
        <h1>{{ t('research-references') }}</h1>
        <p class="research-subtitle">{{ t('research-references-subtitle') }}</p>
        
        <!-- Search Input -->
        <div class="search-container">
          <div class="search-input-wrapper">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search references..." 
              class="search-input"
              @input="onSearchInput"
            />
            <div class="search-icon">🔍</div>
          </div>
          <div v-if="searchQuery" class="search-results-info">
            <p>{{ filteredReferences.length }} result(s) found for "{{ searchQuery }}"</p>
            <button @click="clearSearch" class="clear-search-btn">Clear</button>
          </div>
        </div>
        
        <div class="pagination-info">
          <p>Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalReferences }} references</p>
        </div>
        <div class="references-list">
          <div v-for="(reference, index) in paginatedReferences" :key="index" class="reference-item">
            <p>{{ reference.text }}</p>
            <a v-if="reference.url" :href="reference.url" target="_blank" class="link-button">
              View Source
            </a>
          </div>
        </div>
        
        <!-- Pagination Controls -->
        <div class="pagination-controls">
          <button 
            @click="goToPage(currentPage - 1)" 
            :disabled="currentPage === 1"
            class="pagination-btn"
          >
            Previous
          </button>
          
          <div class="pagination-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              @click="goToPage(page)"
              :class="['pagination-number', { active: page === currentPage }]"
            >
              {{ page }}
            </button>
          </div>
          
          <button 
            @click="goToPage(currentPage + 1)" 
            :disabled="currentPage === totalPages"
            class="pagination-btn"
          >
            Next
          </button>
        </div>
      </div>
    </div>
    <FooterComponent/>
  </template>
  
  <script setup>
  import NavigationBar from '@/components/NavigationBar.vue';
  import FooterComponent from '@/components/Components/FooterComponent.vue';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
  
  const references = ref([
    {
      text: "Abbasi- Shavazi, M., Safari hajataghaiee, S., Sadeghian, H., Noori-Shadkam, M., & Askarishahi, M. (2018). Perceived benefits and barriers of mothers with premature infant to kangaroo mother care: A Case study in Neonatal Intensive Care Unit of Shahid Sadoughi Hospital in Yazd, Iran. International Journal of Pediatrics, Online First.",
      url: "https://doi.org/10.22038/ijp.2018.35249.3096"
    },
    {
      text: "Abd El-Azeem Omran, A. A. E.-F., Abdel Hakim Ahmed, H., & Abd El Sattar Ahmed, M. (2020). Self-Care of Women during Postpartum period in Rural Area. Egyptian Journal of Health Care, 11(1), 59–72.",
      url: "https://doi.org/10.21608/ejhc.2020.72594"
    },
    {
      text: "Abdei-Samad, A., Hassan, S., Amasha, H., & Shalaby, N. (2023). The Effect of Prenatal Educational Guidelines about Postpartum Care on Maternal Coping. Port Said Scientific Journal of Nursing, 10(1), 221–239.",
      url: "https://doi.org/10.21608/pssjn.2023.135161.1196"
    },
    {
      text: "Ahmadinezhad, G. S., Karimi, F. Z., Abdollahi, M., & NaviPour, E. (2024). Association between postpartum depression and breastfeeding self-efficacy in mothers: A systematic review and meta-analysis. BMC Pregnancy and Childbirth, 24(1), 273.",
      url: "https://doi.org/10.1186/s12884-024-06465-4"
    },
    {
      text: "Akbari, H., Yoosefi, M., Pourabbas, M., Weiss, K., Knechtle, B., Vancini, R., Trakada, G., Ben Saad, H., Lavie, C., & Ghram, A. (2022). Association of Ramadan Participation with Psychological Parameters: A Cross-Sectional Study during the COVID-19 Pandemic in Iran. Journal of Clinical Medicine, 11(9), 2346.",
      url: "https://doi.org/10.3390/jcm11092346"
    },
    {
      text: "Akwa, Ashley, A., Ashley. (n.d.). What is the Importance of Educating Women on Postpartum Depression? Social Work Master's Clinical Research Papers, 415.",
      url: "https://ir.stthomas.edu/ssw_mstrp/415?utm_source=ir.stthomas.edu%2Fssw_mstrp%2F415&utm_medium=PDF&utm_campaign=PDFCoverPages"
  },
  {
    text: "Alaem, F., Jalali, A., Almasi, A., Abdi, A., & Khalili, M. (2019). Investigating the effect of group counseling on family stress and anxiety of primiparous mothers during delivery. BioPsychoSocial Medicine, 13(1), 7.",
    url: "https://doi.org/10.1186/s13030-019-0148-1"
  },
  {
    text: "Albanese, A. M., Geller, P. A., Steinkamp, J. M., & Barkin, J. L. (2020). In Their Own Words: A Qualitative Investigation of the Factors Influencing Maternal Postpartum Functioning in the United States. International Journal of Environmental Research and Public Health, 17(17), 6021.",
    url: "https://doi.org/10.3390/ijerph17176021"
  },
  {
    text: "Alshikh Ahmad, H., Alkhatib, A., & Luo, J. (2021). Prevalence and risk factors of postpartum depression in the Middle East: A systematic review and meta–analysis. BMC Pregnancy and Childbirth, 21(1), 542.",
    url: "https://doi.org/10.1186/s12884-021-04016-9"
  },
  {
    text: "Amany Abd El-Fatah Abd El-Azeem Omran, Hanaa Abdel Hakim Ahmed, & Magda Abd El Sattar Ahmed. (2019). Self- Care program for Improving Practices among Postpartum Women in Rural Areas. International Journal of Novel Research in Healthcare and Nursing Novelty Journals, 6(2), 259–272.",
    url: null
  },
  {
    text: "Amany Ali Abd El-Salam, & Eman Seif Soliman Ashour, E. S. S. A. (2020). Effectiveness of Video Assisted Teaching Program on Postpartum Minor Discomforts of Primipara Mothers. American Journal of Nursing Research, 8(2), 142–150.",
    url: "https://doi.org/10.12691/ajnr-8-2-2"
  },
  {
    text: "Anniverno, R., Bramante, A., Mencacci, C., & Durbano, F. (2013). Anxiety Disorders in Pregnancy and the Postpartum Period. In F. Durbano, New Insights into Anxiety Disorders. InTech.",
    url: "https://doi.org/10.5772/52786"
  },
  {
    text: "Anokye, R., Acheampong, E., Budu-Ainooson, A., Obeng, E. I., & Akwasi, A. G. (2018). Prevalence of postpartum depression and interventions utilized for its management. Annals of General Psychiatry, 17(1), 18.",
    url: "https://doi.org/10.1186/s12991-018-0188-0"
  },
  {
    text: "Ansari, N. S., Shah, J., Dennis, C., & Shah, P. S. (2021). Risk factors for postpartum depressive symptoms among fathers: A systematic review and meta‐analysis. Acta Obstetricia et Gynecologica Scandinavica, 100(7), 1186–1199.",
    url: "https://doi.org/10.1111/aogs.14109"
  },
  {
    text: "Asadi, M., Noroozi, M., & Alavi, M. (2021). Exploring the experiences related to postpartum changes: Perspectives of mothers and healthcare providers in Iran. BMC Pregnancy and Childbirth, 21(1).",
    url: "https://doi.org/10.1186/s12884-020-03504-8"
  },
  {
    text: "Ashford, M. T., Olander, E. K., Rowe, H., Fisher, J. R., & Ayers, S. (2018). Feasibility and Acceptability of a Web-Based Treatment with Telephone Support for Postpartum Women With Anxiety: Randomized Controlled Trial. JMIR Mental Health, 5(2), e19.",
    url: "https://doi.org/10.2196/mental.9106"
  },
  {
    text: "Atkinson, J., Smith, V., Carroll, M., Sheaf, G., & Higgins, A. (2021). Perspectives of partners of mothers who experience mental distress in the postnatal period: A systematic review and qualitative evidence synthesis. Midwifery, 93, 102868.",
    url: "https://doi.org/10.1016/j.midw.2020.102868"
  },
  {
    text: "Aydın, R., Barkin, J. L., & Kukulu, K. (2016). Attempts to strengthen maternal functioning in the postpartum period: A literature review. Journal of Human Sciences, 13(3), 5751.",
    url: "https://doi.org/10.14687/jhs.v13i3.4170"
  },
  {
    text: "Bandura, A. (1977). Toward a unifying theory of behavioral change. Psychol Rev, 84, 191–215.",
    url: null
  },
  {
    text: "Bandura, A. (1997). Self-efficacy: The Exercise of Control. WH Freeman.",
    url: null
  },
  {
    text: "Bandura, A. (2012). On the functional properties of perceived self-efficacy revisited. J Manag, 38, 9–44.",
    url: null
  },
  {
    text: "Baran, J., Leszczak, J., Baran, R., Biesiadecka, A., Weres, A., Czenczek-Lewandowska, E., & Kalandyk-Osinko, K. (2021). Prenatal and Postnatal Anxiety and Depression in Mothers during the COVID-19 Pandemic. Journal of Clinical Medicine, 10(14), 3193.",
    url: "https://doi.org/10.3390/jcm10143193"
  },
  {
    text: "Barbic, S. P., MacKirdy, K., Weiss, R., Barrie, A., Kitchin, V., & Lepin, S. (2021). Scoping Review of the Role of Occupational Therapy in the Treatment of Women With Postpartum Depression. Annals of International Occupational Therapy, 4(4).",
    url: "https://doi.org/10.3928/24761222-20210921-02"
  },
  {
    text: "Barnes, C. (2007). Cognitive, Emotional and Environmental Mediators of Early Parenting in High Risk Families. University of Wolverhampton.",
    url: null
  },
  {
    text: "Barnes, C., & Adamson-Macedo, E. (2007). Perceived Maternal Parenting Self-Efficacy (PMP S-E) tool: Development and validation with mothers of hospitalised preterm neonates. J Adv Nurs, 60, 550–560.",
    url: null
  },
  {
    text: "Batt, M. M., Duffy, K. A., Novick, A. M., Metcalf, C. A., & Epperson, C. N. (2020). Is Postpartum Depression Different From Depression Occurring Outside of the Perinatal Period? A Review of the Evidence. Focus, 18(2), 106–119.",
    url: "https://doi.org/10.1176/appi.focus.20190045"
  },
  {
    text: "Bener, A. (2013). Psychological distress among postpartum mothers of preterm infants and associated factors: A neglected public health problem. Revista Brasileira de Psiquiatria, 35(3), 231–236.",
    url: "https://doi.org/10.1590/1516-4446-2012-0821"
  },
  {
    text: "Biaggi, A., Conroy, S., Pawlby, S., & Pariante, C. M. (2016). Identifying the women at risk of antenatal anxiety and depression: A systematic review. Journal of Affective Disorders, 191, 62–77.",
    url: "https://doi.org/10.1016/j.jad.2015.11.014"
  },
  {
    text: "Bliss. (2010). Chance of a Lifetime? Bliss Baby Report. Bliss.",
    url: null
  },
  {
    text: "Bogaerts, A. F. L., Van Den Bergh, B. R. H., Witters, I., & Devlieger, R. (2013). Anxiety during early pregnancy predicts postpartum weight retention in obese mothers. Obesity, 21(9), 1942–1949.",
    url: "https://doi.org/10.1002/oby.20352"
  },
  {
    text: "Boykova, M., & Kenner, C. (2012). International connections: Transition from hospital to home: Post-neonatal intensive care unit discharge: A global perspective. Newborn Infant Nurs Rev, 12, 184–186.",
    url: null
  },
  {
    text: "Bradshaw, H., Riddle, J. N., Salimgaraev, R., Zhaunova, L., & Payne, J. L. (2022). Risk factors associated with postpartum depressive symptoms: A multinational study. Journal of Affective Disorders, 301, 345–351.",
    url: "https://doi.org/10.1016/j.jad.2021.12.121"
  },
  {
    text: "Branquinho, M., Canavarro, M. C., & Fonseca, A. (2020). A Blended Cognitive–Behavioral Intervention for the Treatment of Postpartum Depression: Study Protocol for a Randomized Controlled Trial. International Journal of Environmental Research and Public Health, 17(22), 8631.",
    url: "https://doi.org/10.3390/ijerph17228631"
  },
  {
    text: "Braun, V., & Clarke, V. (2006). Using thematic analysis in psychology. Qual Res Psycho, 3, 77–101.",
    url: null
  },
  {
    text: "Brett, J., Staniszewska, S., Newburn, M., Jones, N., & Taylor, L. (2011). A systematic mapping review of effective interventions for communicating with, supporting and providing information to parents of preterm infants. BMJ Open, 1, e000023.",
    url: null
  },
  {
    text: "British Association of Perinatal Medicine (BAPM). (2001). Categories of Babies Requiring Neonatal Care. In BAPM (Ed.), Standards for Hospitals Providing Neonatal Intensive and High Dependency Care (2nd edn.). BAPM.",
    url: null
  },
  {
    text: "British Association of Perinatal Medicine (BAPM). (2010). Service Standards for Hospitals Providing Neonatal Care (3rd edn.). BAPM.",
    url: null
  },
  {
    text: "Burden, C., Bradley, S., Storey, C., Ellis, A., Heazell, A. E. P., Downe, S., Cacciatore, J., & Siassakos, D. (2016). From grief, guilt pain and stigma to hope and pride – a systematic review and meta-analysis of mixed-method research of the psychosocial impact of stillbirth. BMC Pregnancy and Childbirth, 16(1), 9.",
    url: "https://doi.org/10.1186/s12884-016-0800-8"
  },
  {
    text: "C Sipasulta, Ns. G., Imamah, I. N., & Tanihatu, G. E. (2023). Experiences and Expectations of Post-Partum Mothers Toward Psychosocial Nursing Services for Mothers Who Have Experienced Postpartum Blues in City of Balikpapan. Pharmacognosy Journal, 15(3), 471–478.",
    url: "https://doi.org/10.5530/pj.2023.15.104"
  },
  {
    text: "Cadman, T., Strandberg-Larsen, K., Calas, L., Christiansen, M., Culpin, I., Dadvand, P., De Castro, M., Foraster, M., Fossati, S., Guxens, M., Harris, J. R., Hillegers, M., Jaddoe, V., Lee, Y., Lepeule, J., El Marroun, H., Maule, M., McEachen, R., Moccia, C., … Pedersen, M. (2024). Urban environment in pregnancy and postpartum depression: An individual participant data meta-analysis of 12 European birth cohorts. Environment International, 185, 108453.",
    url: "https://doi.org/10.1016/j.envint.2024.108453"
  },
  {
    text: "Çankaya, S., & Yilmaz, S. D. (2020). Labour Worry and Postpartum Discomfort of Primiparous Mothers in Konya, Turkey. Africa Journal of Nursing and Midwifery, 22(1).",
    url: "https://doi.org/10.25159/2520-5293/4469"
  },
  {
    text: "Chamangasht, M., Akbari Kamrani, M., & Farid, M. (2021). Efficacy of an Early Self-care-Based Education Program on the Self-evaluation of Primiparous Postpartum Mothers: A Randomized Controlled Clinical Trial. Shiraz E-Medical Journal, 22(9).",
    url: "https://doi.org/10.5812/semj.108132"
  },
  {
    text: "Chan, K. L., Leung, W. C., Tiwari, A., Or, K. L., & Ip, P. (2019). Using Smartphone-Based Psychoeducation to Reduce Postnatal Depression Among First-Time Mothers: Randomized Controlled Trial. JMIR mHealth and uHealth, 7(5), e12794.",
    url: "https://doi.org/10.2196/12794"
  },
  {
    text: "Chen, Q., Li, W., Xiong, J., & Zheng, X. (2022). Prevalence and Risk Factors Associated with Postpartum Depression during the COVID-19 Pandemic: A Literature Review and Meta-Analysis. International Journal of Environmental Research and Public Health, 19(4), 2219.",
    url: "https://doi.org/10.3390/ijerph19042219"
  },
  {
    text: "Chinchilla-Ochoa, D., Barriguete Chávez-Peón, P., Farfán-Labonne, B. E., Garza-Morales, S., Leff-Gelman, P., & Flores-Ramos, M. (2019). Depressive symptoms in pregnant women with high trait and state anxiety during pregnancy and postpartum. International Journal of Women's Health, Volume 11, 257–265.",
    url: "https://doi.org/10.2147/ijwh.s194021"
  },
  {
    text: "Choi, H., Yamashita, T., Wada, Y., Narumoto, J., Nanri, H., Fujimori, A., Yamamoto, H., Nishizawa, S., Masaki, D., & Fukui, K. (2010). Factors associated with postpartum depression and abusive behavior in mothers with infants. Psychiatry and Clinical Neurosciences, 64(2), 120–127.",
    url: "https://doi.org/10.1111/j.1440-1819.2010.02063.x"
  },
  {
    text: "Chrzan-Dętkoś, M., Walczak-Kozłowska, T., & Lipowska, M. (2021). The need for additional mental health support for women in the postpartum period in the times of epidemic crisis. BMC Pregnancy and Childbirth, 21(1), 114.",
    url: "https://doi.org/10.1186/s12884-021-03544-8"
  },
  {
    text: "Clements, K., Barfield, W., Ayadi, M., & Wilber, N. (2007). Preterm birth-associated cost of early intervention services: An analysis by gestational age. Pediatrics, 119, e866–e874.",
    url: null
  },
  {
    text: "Clinkscales, N., Golds, L., Berlouis, K., & MacBeth, A. (2023). The effectiveness of psychological interventions for anxiety in the perinatal period: A systematic review and meta‐analysis. Psychology and Psychotherapy: Theory, Research and Practice, 96(2), 296–327.",
    url: "https://doi.org/10.1111/papt.12441"
  },
  {
    text: "Corbally, L., & Wilkinson, M. (2021). The Effect of Mindfulness-Based Interventions on Stress, Depression and Anxiety During the Perinatal Period in Women Without Pre-existing Stress, Depressive or Anxiety Disorders: A Systematic Review and Meta-analysis of Controlled Trials. Mindfulness, 12(10), 2357–2370.",
    url: "https://doi.org/10.1007/s12671-021-01697-3"
  },
  {
    text: "Craig, I., & Adams, C. (2007). Survey shows ongoing crisis in health visiting. Community Pract, 80, 50–53.",
    url: null
  },
  {
    text: "Curtis, L. (2014). Unit Costs of Health and Social Care 2014. PSSRU, University of Kent.",
    url: null
  },
  {
    text: "Dadi, A. F., Miller, E. R., & Mwanri, L. (2020). Postnatal depression and its association with adverse infant health outcomes in low- and middle-income countries: A systematic review and meta-analysis. BMC Pregnancy and Childbirth, 20(1), 416.",
    url: "https://doi.org/10.1186/s12884-020-03092-7"
  },
  {
    text: "Darius Tandon, S., Leis, J. A., Ward, E. A., Snyder, H., Mendelson, T., Perry, D. F., Carter, M., Hamil, J., & Le, H.-N. (2018). Adaptation of an evidence-based postpartum depression intervention: Feasibility and acceptability of mothers and babies 1-on-1. BMC Pregnancy and Childbirth, 18(1), 93.",
    url: "https://doi.org/10.1186/s12884-018-1726-0"
  },
  {
    text: "Dennis, C., & Faux, S. (1999). Development and psychometric testing of the breastfeeding self-efficacy scale. Res Nurs Health, 22, 399–409.",
    url: null
  },
  {
    text: "Dennis, C.-L., Falah-Hassani, K., & Shiri, R. (2017). Prevalence of antenatal and postnatal anxiety: Systematic review and meta-analysis. British Journal of Psychiatry, 210(5), 315–323.",
    url: "https://doi.org/10.1192/bjp.bp.116.187179"
  },
  {
    text: "Department of Health (DH). (2009). Toolkit for High-Quality Neonatal Services. DH.",
    url: null
  },
  {
    text: "Department of Health (DH). (2012). NHS Reference Costs 2011–12. DH. www.gov.uk/government/uploads/system/uploads/attachment_data/file/213060/2011-12-reference-costs-publication.pdf",
    url: null
  },
  {
    text: "Dessì, A., Pianese, G., Mureddu, P., Fanos, V., & Bosco, A. (2024). From Breastfeeding to Support in Mothers' Feeding Choices: A Key Role in the Prevention of Postpartum Depression? Nutrients, 16(14), 2285.",
    url: "https://doi.org/10.3390/nu16142285"
  },
  {
    text: "Didem Türe. (2021). MOTHER-SHAMING MEMORIES: THE ROLE OF MATERNAL SELF-DISCREPANCY ON NEGATIVE EMOTIONS AND SHAMING EXPERIENCES OF MOTHERS. Middle East Technical University.",
    url: null
  },
  {
    text: "Dinari, F., Ershad Sarabi, R., Mashouf, E., & Moulaei, K. (2022). The role of social networks in improving women's self-care during pregnancy and postpartum. Frontiers in Health Informatics, 11(1), 116.",
    url: "https://doi.org/10.30699/fhi.v11i1.371"
  },
  {
    text: "Doonan, C. (2022). There's no formula for a good mother: Shame and estranged maternal labour. Feminist Theory, 23(4), 512–538.",
    url: "https://doi.org/10.1177/14647001211042488"
  },
  {
    text: "Duan, Z., Wang, Y., Jiang, P., Wilson, A., Guo, Y., Lv, Y., Yang, X., Yu, R., Wang, S., Wu, Z., Xia, M., Wang, G., Tao, Y., Xiaohong, L., Ma, L., Shen, H., Sun, J., Deng, W., Yang, Y., & Chen, R. (2020). Postpartum depression in mothers and fathers: A structural equation model. BMC Pregnancy and Childbirth, 20(1), 537.",
    url: "https://doi.org/10.1186/s12884-020-03228-9"
  },
  {
    text: "Dunn, M., Reilly, M., Johnston, A., Hoopes, R., & Abraham, M. (2006). Development and dissemination of potentially better practices for the provision of family-centred care in neonatology: The family-centred care map. Pediatrics, 118(Suppl. 2), 95–107.",
    url: null
  },
  {
    text: "Edhborg, M., Nasreen, H. E., & Kabir, Z. N. (2015). 'I can't stop worrying about everything'—Experiences of rural Bangladeshi women during the first postpartum months. International Journal of Qualitative Studies on Health and Well-Being, 10(1), 26226.",
    url: "https://doi.org/10.3402/qhw.v10.26226"
  },
  {
    text: "Eitenmüller, P., Köhler, S., Hirsch, O., & Christiansen, H. (2022). The Impact of Prepartum Depression and Birth Experience on Postpartum Mother-Infant Bonding: A Longitudinal Path Analysis. Frontiers in Psychiatry, 13, 815822.",
    url: "https://doi.org/10.3389/fpsyt.2022.815822"
  },
  {
    text: "Ellpody, E., Elagamy, M., Gaheen, M., & Ahmed, M. (2023). Perception of Pregnant Women Regarding Self Care Measures during Postpartum Period. Tanta Scientific Nursing Journal, 29(2), 134–153.",
    url: "https://doi.org/10.21608/tsnj.2023.298502"
  },
  {
    text: "Epifanio, M. S., Genna, V., De Luca, C., Roccella, M., & La Grutta, S. (2015). Paternal and Maternal Transition to Parenthood: The Risk of Postpartum Depression and Parenting Stress. Pediatric Reports, 7(2), 5872.",
    url: "https://doi.org/10.4081/pr.2015.5872"
  },
  {
    text: "Escriba-Aguir, V., & Artazcoz, L. (2011). Gender differences in postpartum depression: A longitudinal cohort study. Journal of Epidemiology & Community Health, 65(4), 320–326.",
    url: "https://doi.org/10.1136/jech.2008.085894"
  },
  {
    text: "Esteban-Gonzalo, S., Caballero-Galilea, M., González-Pascual, J. L., Álvaro-Navidad, M., & Esteban-Gonzalo, L. (2021). Anxiety and Worries among Pregnant Women during the COVID-19 Pandemic: A Multilevel Analysis. International Journal of Environmental Research and Public Health, 18(13), 6875.",
    url: "https://doi.org/10.3390/ijerph18136875"
  },
  {
    text: "Fahey, J. O., & Shenassa, E. (2013). Understanding and Meeting the Needs of Women in the Postpartum Period: The Perinatal Maternal Health Promotion Model. Journal of Midwifery & Women's Health, 58(6), 613–621.",
    url: "https://doi.org/10.1111/jmwh.12139"
  },
  {
    text: "Fallon, V., Davies, S. M., Christiansen, P., Harrold, J. A., & Silverio, S. A. (2022). The Postpartum Specific Anxiety Scale: Confirmatory factor analyses and relationships with birth experience. Archives of Women's Mental Health, 25(3), 655–665.",
    url: "https://doi.org/10.1007/s00737-022-01233-9"
  },
  {
    text: "Fallon, V., Halford, J. C. G., Bennett, K. M., & Harrold, J. A. (2016). The Postpartum Specific Anxiety Scale: Development and preliminary validation. Archives of Women's Mental Health, 19(6), 1079–1090.",
    url: "https://doi.org/10.1007/s00737-016-0658-9"
  },
  {
    text: "Fariha Chaudhary. (2021). [M]othering: Exploring Motherhood, Honor-Shame and Patriarchal Convergences in Selected Modern English Fiction by Muslim Writers. Pakistan Journal of Social Sciences, 41(3), 477–486.",
    url: null
  },
  {
    text: "Field, T. (2017). Postpartum Anxiety Prevalence, Predictors and Effects on Child Development: A Review. Journal of Psychiatry and Psychiatric Disorders, 01(02), 86–102.",
    url: "https://doi.org/10.26502/jppd.2572-519x0010"
  },
  {
    text: "Figueiredo, B., Dias, C. C., Brandão, S., Canário, C., & Nunes-Costa, R. (2013). Breastfeeding and postpartum depression: State of the art review. Jornal de Pediatria, 89(4), 332–338.",
    url: "https://doi.org/10.1016/j.jped.2012.12.002"
  },
  {
    text: "Finlayson, K., Crossland, N., Bonet, M., & Downe, S. (2020). What matters to women in the postnatal period: A meta-synthesis of qualitative studies. PLOS ONE, 15(4), e0231415.",
    url: "https://doi.org/10.1371/journal.pone.0231415"
  },
  {
    text: "Finlayson, K., Dixon, A., Smith, C., Dykes, F., & Flacking, R. (2014). Mothers' perceptions of family centred care in neonatal intensive care units. Sex Reprod Healthc, 5, 119–124.",
    url: null
  },
  {
    text: "Forsythe, P., Maher, R., Kirchick, C., & Bieda, A. (2007). Safe discharge for infants with high-risk home environments. Adv Neonatal Care, 7, 69–75.",
    url: null
  },
  {
    text: "Frizelle, Kerry, & Kell, Gabrielle. (2010). A contextual account of motherhood. Psychology in Society, 39, 26–44.",
    url: null
  },
  {
    text: "Fullagar, S., & O'Brien, W. (2014). Social recovery and the move beyond deficit models of depression: A feminist analysis of mid-life women's self-care practices. Social Science & Medicine, 117, 116–124.",
    url: "https://doi.org/10.1016/j.socscimed.2014.07.041"
  },
  {
    text: "Gaal, B., Blatz, S., Dix, J., & Jennings, B. (2008). Discharge planning utilising the discharge train. Adv Neonatal Care, 1, 42–55.",
    url: null
  },
  {
    text: "Garapati, J., Jajoo, S., Aradhya, D., Reddy, L. S., Dahiphale, S. M., & Patel, D. J. (2023). Postpartum Mood Disorders: Insights into Diagnosis, Prevention, and Treatment. Cureus.",
    url: "https://doi.org/10.7759/cureus.42107"
  },
  {
    text: "Garfield, C., Lee, Y., & Kim, H. (2014). Paternal and Maternal Concerns for their very low-birth-weight infants transitioning from the NICU to home. J Perinat Neonat Nurs, 28, 305–312.",
    url: null
  },
  {
    text: "Gastaldon, C., Solmi, M., Correll, C. U., Barbui, C., & Schoretsanitis, G. (2022). Risk factors of postpartum depression and depressive symptoms: Umbrella review of current evidence from systematic reviews and meta-analyses of observational studies. The British Journal of Psychiatry, 221(4), 591–602.",
    url: "https://doi.org/10.1192/bjp.2021.222"
  },
  {
    text: "Gholizadeh Shamasbi, S., Barkin, J. L., Ghanbari-Homayi, S., Eyvazzadeh, O., & Mirghafourvand, M. (2020). The Relationship between Maternal Functioning and Mental Health after Childbirth in Iranian Women. International Journal of Environmental Research and Public Health, 17(5), 1558.",
    url: "https://doi.org/10.3390/ijerph17051558"
  },
  {
    text: "Glazebrook, C., Marlow, N., Israel, C., Croudace, T., Johnson, S., & White, I. (2007). Randomised trial of a parenting intervention during neonatal intensive care. Arch Dis Child Fetal Neonatal Ed, 92, F438–F443.",
    url: null
  },
  {
    text: "Govender, D., Naidoo, S., & Taylor, M. (2020). 'I have to provide for another life emotionally, physically and financially': Understanding pregnancy, motherhood and the future aspirations of adolescent mothers in KwaZulu-Natal South, Africa. BMC Pregnancy and Childbirth, 20(1), 620.",
    url: "https://doi.org/10.1186/s12884-020-03319-7"
  },
  {
    text: "Grande, L. A., Olsavsky, A. K., Erhart, A., Dufford, A. J., Tribble, R., Phan, K. L., & Kim, P. (2021). Postpartum Stress and Neural Regulation of Emotion among First-Time Mothers. Cognitive, Affective, & Behavioral Neuroscience, 21(5), 1066–1082.",
    url: "https://doi.org/10.3758/s13415-021-00914-9"
  },
  {
    text: "Grattan, R. E., London, S. M., & Bueno, G. E. (2024). Perceived pressure to breastfeed negatively impacts postpartum mental health outcomes over time. Frontiers in Public Health, 12, 1357965.",
    url: "https://doi.org/10.3389/fpubh.2024.1357965"
  },
  {
    text: "Gross, D., Rocissano, L., & Roncoli, M. (1989). Maternal confidence during toddlerhood: Comparing preterm and fullterm groups. Res Nurs Health, 12, 1–9.",
    url: null
  },
  {
    text: "Grunberg, V. A., Geller, P. A., Durham, K., Bonacquisti, A., & Barkin, J. L. (2022). Motherhood and Me (Mom-Me): The Development of an Acceptance-Based Group for Women with Postpartum Mood and Anxiety Symptoms. Journal of Clinical Medicine, 11(9), 2345.",
    url: "https://doi.org/10.3390/jcm11092345"
  },
  {
    "text": "Gupta, A., Pajai, S., Gupta, A., Singh Thakur, A., Muneeba, S., Batra, N., & Patel, D. J. (2024). In the Shadows of Motherhood: A Comprehensive Review of Postpartum Depression Screening and Intervention Practices. Cureus.",
    "url": "https://doi.org/10.7759/cureus.54245"
  },
  {
    "text": "Gutiérrez Hermoso, L., Catalá Mesón, P., Écija Gallardo, C., Marín Morales, D., & Peñacoba Puente, C. (2023). Mother-Child Bond through Feeding: A Prospective Study including Neuroticism, Pregnancy Worries and Post-Traumatic Symptomatology. International Journal of Environmental Research and Public Health, 20(3), 2115.",
    "url": "https://doi.org/10.3390/ijerph20032115"
  },
  {
    "text": "Hada, A., Ohashi, Y., Usui, Y., & Kitamura, T. (2024). Typology of Parent-to-Child Emotions: A Study of Japanese Parents of a Foetus up to a 12-Year-Old Child. Healthcare, 12(9), 881.",
    "url": "https://doi.org/10.3390/healthcare12090881"
  },
  {
    "text": "Hahn-Holbrook, J., Cornwell-Hinrichs, T., & Anaya, I. (2018). Economic and Health Predictors of National Postpartum Depression Prevalence: A Systematic Review, Meta-analysis, and Meta-Regression of 291 Studies from 56 Countries. Frontiers in Psychiatry, 8, 248.",
    "url": "https://doi.org/10.3389/fpsyt.2017.00248"
  },
  {
    "text": "Hannon, S., Newnham, E., Hannon, K., Wuytack, F., Johnson, L., McEvoy, E., & Daly, D. (2022). Positive postpartum well‐being: What works for women. Health Expectations, 25(6), 2971–2981.",
    "url": "https://doi.org/10.1111/hex.13605"
  },
  {
    "text": "Harrison, V., Moore, D., & Lazard, L. (2020). Supporting perinatal anxiety in the digital age; a qualitative exploration of stressors and support strategies. BMC Pregnancy and Childbirth, 20(1).",
    "url": "https://doi.org/10.1186/s12884-020-02990-0"
  },
  {
    "text": "Heidari, H., Hasanpour, M., & Fooladi, M. (2012). The Iranian Parents of Premature Infants in NICU Experience Stigma of Shame. Medical Archives, 66(1), 35.",
    "url": "https://doi.org/10.5455/medarh.2012.66.35-40"
  },
  {
    "text": "Hess, C., Teti, D., & Hussey-Gardner, B. (2004). Self-efficacy and parenting of high risk infants: The moderating role of parent knowledge of infant development. J Appl Dev Psychol, 25, 423–437.",
    "url": null
  },
  {
    "text": "Hesselink, G., Flink, M., Olsson, M., Barach, P., Dudzik-Urbaniak, E., & Orrego, C. (2012). Are patients discharged with care? A qualitative study of perceptions and experiences of patients, family members and care providers. BMJ Qual Saf, 21(Suppl. 1), 39–49.",
    "url": null
  },
  {
    "text": "Høifødt, R. S., Nordahl, D., Landsem, I. P., Csifcsák, G., Bohne, A., Pfuhl, G., Rognmo, K., Braarud, H. C., Goksøyr, A., Moe, V., Slinning, K., & Wang, C. E. A. (2020). Newborn Behavioral Observation, maternal stress, depressive symptoms and the mother-infant relationship: Results from the Northern Babies Longitudinal Study (NorBaby). BMC Psychiatry, 20(1), 300.",
    "url": "https://doi.org/10.1186/s12888-020-02669-y"
  },
  {
    "text": "Huang, L., Zhao, Y., Qiang, C., & Fan, B. (2018). Is cognitive behavioral therapy a better choice for women with postnatal depression? A systematic review and meta-analysis. PLOS ONE, 13(10), e0205243.",
    "url": "https://doi.org/10.1371/journal.pone.0205243"
  },
  {
    "text": "Hubert, S., & Aujoulat, I. (2018). Parental Burnout: When Exhausted Mothers Open Up. Frontiers in Psychology, 9, 1021.",
    "url": "https://doi.org/10.3389/fpsyg.2018.01021"
  },
  {
    "text": "Huizink, A. C., Menting, B., De Moor, M. H. M., Verhage, M. L., Kunseler, F. C., Schuengel, C., & Oosterman, M. (2017). From prenatal anxiety to parenting stress: A longitudinal study. Archives of Women’s Mental Health, 20(5), 663–672.",
    "url": "https://doi.org/10.1007/s00737-017-0746-5"
  },
  {
    "text": "Hutchens, B. F., & Kearney, J. (2020). Risk Factors for Postpartum Depression: An Umbrella Review. Journal of Midwifery & Women’s Health, 65(1), 96–108.",
    "url": "https://doi.org/10.1111/jmwh.13067"
  },
  {
    "text": "Incollingo Rodriguez, A. C., Smieszek, S. M., Nippert, K. E., & Tomiyama, A. J. (2020). Pregnant and postpartum women’s experiences of weight stigma in healthcare. BMC Pregnancy and Childbirth, 20(1), 499.",
    "url": "https://doi.org/10.1186/s12884-020-03202-5"
  },
  {
    "text": "Indah Christiana. (n.d.). Effort To Prevent Post Partum Depression: Scoping Review. International Conference on Sustainable Health Promotion, 3(1), 96–112.",
    "url": null
  },
  {
    "text": "Ingram, J., Blair, P. S., Powell, J. E., Manns, S., Burden, H., Pontin, D., Redshaw, M., Beasant, L., Rose, C., Johnson, D., Gaunt, D., & Fleming, P. (2016). Preparing for Home: A before-and-after study to investigate the effects of a neonatal discharge package aimed at increasing parental knowledge, understanding and confidence in caring for their preterm infant before and after discharge from hospital. Health Services and Delivery Research, 4(10), 1–114.",
    "url": "https://doi.org/10.3310/hsdr04100"
  },
  {
    "text": "Jackson, L., De Pascalis, L., Harrold, J. A., Fallon, V., & Silverio, S. A. (2021). Postpartum women’s psychological experiences during the COVID-19 pandemic: A modified recurrent cross-sectional thematic analysis. BMC Pregnancy and Childbirth, 21(1), 625.",
    "url": "https://doi.org/10.1186/s12884-021-04071-2"
  },
  {
    "text": "Jackson, L., De Pascalis, L., Harrold, J., & Fallon, V. (2021). Guilt, shame, and postpartum infant feeding outcomes: A systematic review. Maternal & Child Nutrition, 17(3), e13141.",
    "url": "https://doi.org/10.1111/mcn.13141"
  },
  {
    "text": "Jacob, M. L., & Storch, E. A. (2013). Postpartum anxiety disorders. Asian Pacific Journal of Reproduction, 2(1), 63–68.",
    "url": "https://doi.org/10.1016/S2305-0500(13)60119-0"
  },
  {
    "text": "Jefferies, A. (2014). Going home: Facilitating discharge of the preterm infant. Paediatr Child Health, 19, 31–42.",
    "url": null
  },
  {
    "text": "Jimba, M., Nabae, K., & Wakai, S. (2004). Avian influenza: A chance to strengthen agriculture-health links. The Lancet, 363(9414), 1078.",
    "url": "https://doi.org/10.1016/s0140-6736(04)15859-5"
  },
  {
    "text": "Johansson, M., Benderix, Y., & Svensson, I. (2020). Mothers’ and fathers’ lived experiences of postpartum depression and parental stress after childbirth: A qualitative study. International Journal of Qualitative Studies on Health and Well-Being, 15(1), 1722564.",
    "url": "https://doi.org/10.1080/17482631.2020.1722564"
  },
  {
    "text": "Johnston, K., Gooch, K., Korol, E., Vo, P., Eyawo, O., & Bradt, P. (2014). The economic burden of prematurity in Canada. BMC Pediatrics, 14, 93.",
    "url": null
  },
  {
    "text": "Jones, T., & Prinz, R. (2005). Potential roles of parental self-efficacy in parent and child adjustment: A review. Clin Psychol Rev, 25, 341–363.",
    "url": null
  },
  {
    "text": "Kim, T. H., Connolly, J. A., & Tamim, H. (2014). The effect of social support around pregnancy on postpartum depression among Canadian teen mothers and adult mothers in the maternity experiences survey. BMC Pregnancy and Childbirth, 14(1), 162.",
    "url": "https://doi.org/10.1186/1471-2393-14-162"
  },
  {
    "text": "Kinser, P. A., Jallo, N., Amstadter, A. B., Thacker, L. R., Jones, E., Moyer, S., Rider, A., Karjane, N., & Salisbury, A. L. (2021). Depression, Anxiety, Resilience, and Coping: The Experience of Pregnant and New Mothers During the First Few Months of the COVID-19 Pandemic. Journal of Women’s Health, 30(5), 654–664.",
    "url": "https://doi.org/10.1089/jwh.2020.8866"
  },
  {
    "text": "Kinser, P., Jallo, N., Moyer, S., Weinstock, M., Barrett, D., Mughal, N., Stevens, L., & Rider, A. (2022). “It’s always hard being a mom, but the pandemic has made everything harder”: A qualitative exploration of the experiences of perinatal women during the COVID-19 pandemic. Midwifery, 109, 103313.",
    "url": "https://doi.org/10.1016/j.midw.2022.103313"
  },
  {
    "text": "Kleiman, K., & Waller, H. (2023). The Art of Holding Perinatal Women in Distress. Women’s Health Reports, 4(1), 111–117.",
    "url": "https://doi.org/10.1089/whr.2022.0083"
  },
  {
    "text": "Kornfield, S. L., White, L. K., Waller, R., Njoroge, W., Barzilay, R., Chaiyachati, B. H., Himes, M. M., Rodriguez, Y., Riis, V., Simonette, K., Elovitz, M. A., & Gur, R. E. (2021). Risk And Resilience Factors Influencing Postpartum Depression And Mother-Infant Bonding During COVID-19: Study examines postpartum depression and mother-infant bonding during the COVID-19 pandemic. Health Affairs, 40(10), 1566–1574.",
    "url": "https://doi.org/10.1377/hlthaff.2021.00803"
  },
  {
    "text": "Kristensen, I. H., Simonsen, M., Trillingsgaard, T., & Kronborg, H. (2017). Video feedback promotes relations between infants and vulnerable first-time mothers: A quasi-experimental study. BMC Pregnancy and Childbirth, 17(1), 379.",
    "url": "https://doi.org/10.1186/s12884-017-1568-1"
  },
  {
    "text": "Kumar, D., Hameed, W., & Avan, B. I. (2023). Comparing the effectiveness of mother-focused interventions to that of mother-child focused interventions in improving maternal postpartum depression outcomes: A systematic review. PLOS ONE, 18(12), e0295955.",
    "url": "https://doi.org/10.1371/journal.pone.0295955"
  },
  {
    "text": "Lambermon, F. J., Van Duijnhoven, N. T. L., Kremer, J. A. M., & Dedding, C. W. M. (2021). Mothers’ experiences of client-centred flexible planning in home-based postpartum care: A promising tool to meet their diverse and dynamic needs. Midwifery, 102, 103068.",
    "url": "https://doi.org/10.1016/j.midw.2021.103068"
  },
  {
    "text": "Lanes, A., Kuk, J. L., & Tamim, H. (2011). Prevalence and characteristics of Postpartum Depression symptomatology among Canadian women: A cross-sectional study. BMC Public Health, 11(1), 302.",
    "url": "https://doi.org/10.1186/1471-2458-11-302"
  },
  {
    "text": "Lax, E. S., Graziosi, M., Gioia, A. N., Arunagiri, V., & Novak, S. A. (2023). “More Than Just a Manicure” Qualitative Experiences of Maternal Self-Care During COVID-19. Women’s Health Reports, 4(1), 571–583.",
    "url": "https://doi.org/10.1089/whr.2023.0081"
  },
  {
    "text": "Lee, D.-E., Suh, H.-W., Park, H.-S., Youn, I., Park, M., & Seo, J. (2022). Mothers’ Experiences of Childbirth and Perspectives on Korean Medicine-Based Postpartum Care in Korea: A Qualitative Study. International Journal of Environmental Research and Public Health, 19(9), 5332.",
    "url": "https://doi.org/10.3390/ijerph19095332"
  },
  {
    "text": "Leeming, D., Marshall, J., & Hinsliff, S. (2022). Self‐conscious emotions and breastfeeding support: A focused synthesis of UK qualitative research. Maternal & Child Nutrition, 18(1), e13270.",
    "url": "https://doi.org/10.1111/mcn.13270"
  },
  {
    "text": "Leerkes, E., & Crockenberg, S. (2002). The development of maternal self-efficacy and its impact on maternal behaviour. Infancy, 3, 227–247.",
    "url": null
  },
  {
    "text": "Leigh, B., & Milgrom, J. (2008). Risk factors for antenatal depression, postnatal depression and parenting stress. BMC Psychiatry, 8(1), 24.",
    "url": "https://doi.org/10.1186/1471-244X-8-24"
  },
  {
    "text": "Li, W., Ng, F. F.-Y., & Chiu, C.-D. (2024). When Parents Are at Fault: Development and Validation of the Parental Guilt and Shame Proneness Scale. Journal of Personality Assessment, 106(5), 595–608.",
    "url": "https://doi.org/10.1080/00223891.2024.2311208"
  },
  {
    "text": "Linsu, T., Sailaxmi, G., & Jithin Thomas, P. (2018). Interventions for Mothers with Postpartum Depression: A Systematic Review. International Journal of Depression and Anxiety, 1(1).",
    "url": "https://doi.org/10.23937/ijda-2017/1710002"
  },
  {
    "text": "Liu, C., Chen, H., Zhou, F., Long, Q., Wu, K., Lo, L.-M., Hung, T.-H., Liu, C.-Y., & Chiou, W.-K. (2022). Positive intervention effect of mobile health application based on mindfulness and social support theory on postpartum depression symptoms of puerperae. BMC Women’s Health, 22(1), 413.",
    "url": "https://doi.org/10.1186/s12905-022-01996-4"
  },
  {
    "text": "Loughnan, S. A., Butler, C., Sie, A. A., Grierson, A. B., Chen, A. Z., Hobbs, M. J., Joubert, A. E., Haskelberg, H., Mahoney, A., Holt, C., Gemmill, A. W., Milgrom, J., Austin, M.-P., Andrews, G., & Newby, J. M. (2019). A randomised controlled trial of ‘MUMentum postnatal’: Internet-delivered cognitive behavioural therapy for anxiety and depression in postpartum women. Behaviour Research and Therapy, 116, 94–103.",
    "url": "https://doi.org/10.1016/j.brat.2019.03.001"
  },
  {
    "text": "Lowe, N. (1993). Maternal confidence for labour: Development of the childbirth self-efficacy inventory. Res Nurs Health, 16, 141–149.",
    "url": null
  },
  {
    "text": "M, D., R, K., R, A., & M, R. (2018). Mother’s Personal Care during Post Partum Period. Egyptian Journal of Health Care, 9(2), 48–60.",
    "url": "https://doi.org/10.21608/ejhc.2018.10107"
  },
  {
    "text": "Maimburg, R. D., & Væth, M. (2015). Postpartum depression among first-time mothers – results from a parallel randomised trial. Sexual & Reproductive Healthcare, 6(2), 95–100.",
    "url": "https://doi.org/10.1016/j.srhc.2015.01.003"
  },
  {
    "text": "Mamrath, S., Greenfield, M., Fernandez Turienzo, C., Fallon, V., & Silverio, S. A. (2024). Experiences of postpartum anxiety during the COVID-19 pandemic: A mixed methods study and demographic analysis. PLOS ONE, 19(3), e0297454.",
    "url": "https://doi.org/10.1371/journal.pone.0297454"
  },
  {
    "text": "Mangham, K., Hall, C., Jackson, E., Owens, O., & Mahadevia, P. (2009). The cost of pre-term birth throughout childhood in England and Wales. Pediatrics, 123, e312–e327.",
    "url": null
  },
  {
    "text": "Marcelina, L. A., Rachmawati, I. N., & Kurniawati, W. (2023). Self-Care and Unpleasant Symptoms in Indonesian Postpartum Mothers with Twin Babies: A Case Report. Malahayati Nursing Journal, 5(5), 1472–1477.",
    "url": "https://doi.org/10.33024/mnj.v5i5.9140"
  },
  {
    "text": "Maryatun, Bangkit Ary Pratama, Indarwati Indarwati, Annisa Andriyani, Ratnawati Kusumaningsih, Sri Mulyani, Endang Sri Wahyuni, & Nur Wulan Agustina. (2023). EVALUATION OF A SANTUN SEJATI WEB-BASED APPLICATION: MOTHER AND BABY CARE WITH COGNITIVE BEHAVIOUR THERAPY APPROACH. International Journal of Public Health Excellence (IJPHE), 3(1), 38–44.",
    "url": "https://doi.org/10.55299/ijphe.v3i1.411"
  },
  {
    "text": "Masomeh Mehri, Mina Iravani, Mehdi Sayyah Bargard, & Mohammad Hosein Haghighizadeh. (2019). Effectiveness of Cognitive Behavioral Therapy-based self-management on depression in Pregnant Women: A Randomized Controlled Trial. Journal of Biochemical Technology, 10(3), 92–97.",
    "url": null
  },
  {
    "text": "Maxwell, D., Robinson, S. R., & Rogers, K. (2019). “I keep it to myself”: A qualitative meta‐interpretive synthesis of experiences of postpartum depression among marginalised women. Health & Social Care in the Community, 27(3), e23–e36.",
    "url": "https://doi.org/10.1111/hsc.12645"
  },
  {
    "text": "McCarthy, M., Houghton, C., & Matvienko-Sikar, K. (2021). Women’s experiences and perceptions of anxiety and stress during the perinatal period: A systematic review and qualitative evidence synthesis. BMC Pregnancy and Childbirth, 21(1).",
    "url": "https://doi.org/10.1186/s12884-021-04271-w"
  },
  {
    "text": "McGrath, J. (2012). Strategies to support the transition to home. J Perinat Neonat Nurs, 26, 8–9.",
    "url": null
  },
  {
    "text": "McLeish, J., Harvey, M., Redshaw, M., & Alderdice, F. (2021). A qualitative study of first time mothers’ experiences of postnatal social support from health professionals in England. Women and Birth, 34(5), e451–e460.",
    "url": "https://doi.org/10.1016/j.wombi.2020.10.012"
  },
  {
    "text": "McNamara, J., Townsend, M. L., & Herbert, J. S. (2019). A systemic review of maternal wellbeing and its relationship with maternal fetal attachment and early postpartum bonding. PLOS ONE, 14(7), e0220032.",
    "url": "https://doi.org/10.1371/journal.pone.0220032"
  },
  {
    "text": "McSorley, L., Deighton-Smith, N., Budds, K., & Wang, X. (2022). “Hang in there mama!” The role of online parenting forums in maternal mental health. Cyberpsychology: Journal of Psychosocial Research on Cyberspace, 16(4).",
    "url": "https://doi.org/10.5817/CP2022-4-11"
  },
  {
    "text": "Melnyk, B., Feinstein, N., Alpert-Gillish, L., Fairbanks, E., Crean, H., & Sinkin, R. (2006). Reducing premature infants’ length of stay and improving parents’ mental health outcomes with the Creating Opportunities for Parent Empowerment (COPE) Neonatal Intensive Care Unit Program: A randomised controlled trial. Pediatrics, 118, e1414–e1427.",
    "url": null
  },
  {
    "text": "Missler, M., Donker, T., Beijers, R., Ciharova, M., Moyse, C., De Vries, R., Denissen, J., & Van Straten, A. (2021). Universal prevention of distress aimed at pregnant women: A systematic review and meta-analysis of psychological interventions. BMC Pregnancy and Childbirth, 21(1), 276.",
    "url": "https://doi.org/10.1186/s12884-021-03752-2"
  },
  {
    "text": "Missler, M., Van Straten, A., Denissen, J., Donker, T., & Beijers, R. (2020). Effectiveness of a psycho-educational intervention for expecting parents to prevent postpartum parenting stress, depression and anxiety: A randomized controlled trial. BMC Pregnancy and Childbirth, 20(1).",
    "url": "https://doi.org/10.1186/s12884-020-03341-9"
  },
  {
    "text": "Mitchell, C., Notterman, D., Brooks-Gunn, J., Hobcraft, J., Garfinkel, I., Jaeger, K., Kotenko, I., & McLanahan, S. (2011). Role of mother’s genes and environment in postpartum depression. Proceedings of the National Academy of Sciences, 108(20), 8189–8193.",
    "url": "https://doi.org/10.1073/pnas.1014129108"
  },
  {
    "text": "Modak, A., Ronghe, V., Gomase, K. P., Mahakalkar, M. G., & Taksande, V. (2023a). A Comprehensive Review of Motherhood and Mental Health: Postpartum Mood Disorders in Focus. Cureus.",
    "url": "https://doi.org/10.7759/cureus.46209"
  },
  {
    "text": "Modak, A., Ronghe, V., Gomase, K. P., Mahakalkar, M. G., & Taksande, V. (2023b). A Comprehensive Review of Motherhood and Mental Health: Postpartum Mood Disorders in Focus. Cureus.",
    "url": "https://doi.org/10.7759/cureus.46209"
  },
  {
    "text": "Mokhtari, F., Bahadoran, P., & Baghersad, Z. (2018). Effectiveness of postpartum homecare program as a new method on mothers’ knowledge about the health of the mother and the infant. Iranian Journal of Nursing and Midwifery Research, 23(4), 316.",
    "url": "https://doi.org/10.4103/Ijnmr.ijnmr_48_17"
  },
  {
    "text": "Molgora, S., & Accordini, M. (2020). Motherhood in the Time of Coronavirus: The Impact of the Pandemic Emergency on Expectant and Postpartum Women’s Psychological Well-Being. Frontiers in Psychology, 11.",
    "url": "https://doi.org/10.3389/fpsyg.2020.567155"
  },
  {
    "text": "Molnár, J., Kósa, K., Fekete, Z., & Münnich, Á. (2018). Postpartum Anxiety and Intrapsychic Vulnerability Reflected by Early Maladaptive Schemas. European Journal of Mental Health, 13(1), 70–81.",
    "url": "https://doi.org/10.5708/ejmh.13.2018.1.6"
  },
  {
    "text": "Moore, D., Ayers, S., & Drey, N. (2016). A Thematic Analysis of Stigma and Disclosure for Perinatal Depression on an Online Forum. JMIR Mental Health, 3(2), e18.",
    "url": "https://doi.org/10.2196/mental.5611"
  },
  {
    "text": "Moyer, V., Papile, L.-A., Eichenwald, C., Giardino, A., Khan, M., & Singh, H. (2013). An intervention to improve transitions from NICU to ambulatory care: Quasi-experimental study. BMJ Qual Saf, 23, e3.",
    "url": null
  },
  {
    "text": "Muzik, M., & Rosenblum, K. (2016). A Mental Health and Parenting Intervention for Adolescent and Young Adult Mothers and their Infants. Journal of Depression and Anxiety, 5(3).",
    "url": "https://doi.org/10.4172/2167-1044.1000233"
  },
  {
    "text": "Muziki, J. d’Amour, Uwera, T., Niyonsenga, J., Nshimiyimana, A., Sebatukura, S. G., & Mutabaruka, J. (2022). A qualitative analysis of negative feelings among incarcerated filicide mothers in Rwanda. BMC Psychiatry, 22(1), 432.",
    "url": "https://doi.org/10.1186/s12888-022-04081-0"
  },
  {
    "text": "Nahed Ahmed Hussein, Nagat Salah Shalaby, & Inas Mohamed Abd-Allah. (2018a). Postpartum Learning Needs as Perceived by Mothers at Suez Canal University Hospital. IOSR Journal of Nursing and Health Science, 7(5), 47–53.",
    "url": "https://doi.org/10.9790/1959-0705024753"
  },
  {
    "text": "Nair, S. S., & Kalarivayil, R. (2023). Mothering and Radical Selfcare: An Autoethnography of Participating in a Facebook Parenting Group. Feminist Encounters: A Journal of Critical Studies in Culture and Politics, 7(1), 12.",
    "url": "https://doi.org/10.20897/femenc/12890"
  },
  {
    "text": "Nath, S., Pearson, R. M., Moran, P., Pawlby, S., Molyneaux, E., Challacombe, F. L., & Howard, L. M. (2019). The association between prenatal maternal anxiety disorders and postpartum perceived and observed mother-infant relationship quality. Journal of Anxiety Disorders, 68, 102148.",
    "url": "https://doi.org/10.1016/j.janxdis.2019.102148"
  },
  {
    "text": "National Institute for Health and Care Excellence (NICE). (2010). Specialist Neonatal Care Quality Standard. NICE.",
    "url": null
  },
  {
    "text": "Newby, J. M., Werner-Seidler, A., Black, M. J., Hirsch, C. R., & Moulds, M. L. (2021). Content and Themes of Repetitive Thinking in Postnatal First-Time Mothers. Frontiers in Psychology, 12.",
    "url": "https://doi.org/10.3389/fpsyg.2021.586538"
  },
  {
    "text": "NewFAMS team Creators/Copyright Holders, Hughes, C., NewFAMS team Contributors, Foley, S., Devine, R. T., Ribner, A., Kyriakou, L., Boddington, L., & Holmes, E. A. (2020). Worrying in the wings? Negative emotional birth memories in mothers and fathers show similar associations with perinatal mood disturbance and delivery mode. Archives of Women’s Mental Health, 23(3), 371–377.",
    "url": "https://doi.org/10.1007/s00737-019-00973-5"
  },
  {
    "text": "Novak Lauš, K. (2017). Structure-Function Relationship of Changes in Visual Field Indices with Quadrant and Average Retinal Nerve Fiber Layer Thickness in the Eyes with Exfoliation. Acta Clinica Croatica.",
    "url": "https://doi.org/10.20471/acc.2017.56.04.05"
  },
  {
    "text": "Nurhayati Dawenan, F., & Shanti, P. (2022). The Correlation Between Husbands’ Social Support and Anxiety in Mothers Who Have Experienced Mom-Shaming in Malang City. KnE Social Sciences.",
    "url": "https://doi.org/10.18502/kss.v7i1.10205"
  },
  {
    "text": "Nweke, M., Ukwuoma, M., Adiuku-Brown, A. C., Ugwu, P., & Nseka, E. (2022). Characterization and stratification of the correlates of postpartum depression in sub-Saharan Africa: A systematic review with meta-analysis. Women’s Health, 18, 17455057221118773.",
    "url": "https://doi.org/10.1177/17455057221118773"
  },
  {
    "text": "O’Dea, G. A., Youssef, G. J., Hagg, L. J., Francis, L. M., Spry, E. A., Rossen, L., Smith, I., Teague, S. J., Mansour, K., Booth, A., Davies, S., Hutchinson, D., & Macdonald, J. A. (2023). Associations between maternal psychological distress and mother-infant bonding: A systematic review and meta-analysis. Archives of Women’s Mental Health, 26(4), 441–452.",
    "url": "https://doi.org/10.1007/s00737-023-01332-1"
  },
  {
    "text": "Office for National Statistics (ONS). (n.d.). Data for 2007–2008. Retrieved 4 July 2011, from www.statistics.gov.uk/downloads/theme_health/gestation-spec-infant-mort-tables-07-08.xls#Table3!A1",
    "url": null
  },
  {
    "text": "Oktafia, R., Rahmayanti, R., Maghpira, D. A., & Indriastuti, N. A. (2022). Psychosocial Condition and Parenting Self-Efficacy Among Postpartum Mothers. Jurnal Aisyah : Jurnal Ilmu Kesehatan, 7(S2), 241–246.",
    "url": "https://doi.org/10.30604/jika.v7iS2.1435"
  },
  {
    "text": "Osman, H., Saliba, M., Chaaya, M., & Naasan, G. (2014). Interventions to reduce postpartum stress in first-time mothers: A randomized-controlled trial. BMC Women’s Health, 14(1), 125.",
    "url": "https://doi.org/10.1186/1472-6874-14-125"
  },
  {
    "text": "Ospina Romero, A. M., Muñoz Rodríguez, L., & Ruíz De Cárdenas, C. H. (2012). Coping and adaptation process during puerperium. Colombia Medica, 43(2), 167–174.",
    "url": "https://doi.org/10.25100/cm.v43i2.1146"
  },
  {
    "text": "Pedersen, S. C., Maindal, H. T., & Ryom, K. (2021). “I Wanted to Be There as a Father, but I Couldn’t”: A Qualitative Study of Fathers’ Experiences of Postpartum Depression and Their Help-Seeking Behavior. American Journal of Men’s Health, 15(3), 15579883211024375.",
    "url": "https://doi.org/10.1177/15579883211024375"
  },
  {
    "text": "Peñacoba Puente, C., Suso-Ribera, C., Blanco Rico, S., Marín, D., San Román Montero, J., & Catalá, P. (2021). Is the Association between Postpartum Depression and Early Maternal–Infant Relationships Contextually Determined by Avoidant Coping in the Mother? International Journal of Environmental Research and Public Health, 18(2), 562.",
    "url": "https://doi.org/10.3390/ijerph18020562"
  },
  {
    "text": "Pereira, D., Wildenberg, B., Gaspar, A., Cabaços, C., Madeira, N., Macedo, A., & Pereira, A. T. (2022). The Impact of COVID-19 on Anxious and Depressive Symptomatology in the Postpartum Period. International Journal of Environmental Research and Public Health, 19(13), 7833.",
    "url": "https://doi.org/10.3390/ijerph19137833"
  },
  {
    "text": "Perrella, S. L., Dix-Matthews, A., Williams, J., Rea, A., & Geddes, D. T. (2022). Breastfeeding and Maternal Perceptions of Infant Sleep, Settle and Cry Patterns in the First 9 Months. International Journal of Environmental Research and Public Health, 19(20), 13098.",
    "url": "https://doi.org/10.3390/ijerph192013098"
  },
  {
    "text": "Petrou, S., & Khan, K. (2012). Economic costs associated with moderate and late pre-term birth: Primary and secondary evidence. Semin Fetal Neonatal Med, 17, 170–178.",
    "url": null
  },
  {
    "text": "Pettman, D., O’Mahen, H., Blomberg, O., Svanberg, A. S., Von Essen, L., & Woodford, J. (2023). Effectiveness of cognitive behavioural therapy-based interventions for maternal perinatal depression: A systematic review and meta-analysis. BMC Psychiatry, 23(1), 208.",
    "url": "https://doi.org/10.1186/s12888-023-04547-9"
  },
  {
    "text": "PhD, RN, College of Health Sciences, Dr. Yanga’s Colleges Inc., Bocaue, Bulacan, Philippines, P De Jesus, R., Delos Santos Iii, A., DNS, College of Health Sciences, Dr. Yanga’s Colleges Inc., Bocaue, Bulacan, Philippines, Tiongaon, M., & DNM, College of Health Sciences, Dr. Yanga’s Colleges Inc., Bocaue, Bulacan, Philippines. (2024). Struggling against Internal Turmoil: Exploring the Lived Realities of Women who Suffered from Postpartum Depression. Journal of Womens Healthcare & Midwifery Research, 1–9.",
    "url": "https://doi.org/10.47363/JWHMR/2024(3)118"
  },
  {
    "text": "Pierson, M. E., Prenoveau, J. M., Craske, M. G., Netsi, E., & Stein, A. (2017). Psychometric properties of the Generalized Anxiety Disorder Questionnaire—IV (GAD-Q-IV) in postpartum mothers. Psychological Assessment, 29(11), 1391–1399.",
    "url": "https://doi.org/10.1037/pas0000443"
  },
  {
    "text": "Pilarska, N., Siemińska, N., & Skonieczna, W. (n.d.). Self-care and risk of postpartum depression.",
    "url": "https://doi.org/10.34767/PFP.2023.01.02"
  },
  {
    "text": "Power, C., Williams, C., & Brown, A. (2022). Physical and Psychological Childbirth Experiences and Early Infant Temperament. Frontiers in Psychology, 13, 792392.",
    "url": "https://doi.org/10.3389/fpsyg.2022.792392"
  },
  {
    "text": "Poyatos‐León, R., García‐Hermoso, A., Sanabria‐Martínez, G., Álvarez‐Bueno, C., Cavero‐Redondo, I., & Martínez‐Vizcaíno, V. (2017). Effects of exercise‐based interventions on postpartum depression: A meta‐analysis of randomized controlled trials. Birth, 44(3), 200–208.",
    "url": "https://doi.org/10.1111/birt.12294"
  },
  {
    "text": "Prenoveau, J., Craske, M., Counsell, N., West, V., Davies, B., Cooper, P., Rapa, E., & Stein, A. (2013). POSTPARTUM GAD IS A RISK FACTOR FOR POSTPARTUM MDD: THE COURSE AND LONGITUDINAL RELATIONSHIPS OF POSTPARTUM GAD AND MDD. Depression and Anxiety, 30(6), 506–514.",
    "url": "https://doi.org/10.1002/da.22040"
  },
  {
    "text": "Purwati, Y., & Kustiningsih, K. (2018). Benefit of Childbirth Care Counseling for Mood of Postpartum Period. Jurnal Kesehatan Masyarakat, 13(3), 356–365.",
    "url": "https://doi.org/10.15294/kemas.v13i3.11275"
  },
  {
    "text": "Qi, W., Zhao, F., Liu, Y., Li, Q., & Hu, J. (2021). Psychosocial risk factors for postpartum depression in Chinese women: A meta-analysis. BMC Pregnancy and Childbirth, 21(1), 174.",
    "url": "https://doi.org/10.1186/s12884-021-03657-0"
  },
  {
    "text": "Qin, X., Liu, C., Zhu, W., Chen, Y., & Wang, Y. (2022). Preventing Postpartum Depression in the Early Postpartum Period Using an App-Based Cognitive Behavioral Therapy Program: A Pilot Randomized Controlled Study. International Journal of Environmental Research and Public Health, 19(24), 16824.",
    "url": "https://doi.org/10.3390/ijerph192416824"
  },
  {
    "text": "Racine, N., Devereaux, C., Cooke, J. E., Eirich, R., Zhu, J., & Madigan, S. (2021). Adverse childhood experiences and maternal anxiety and depression: A meta-analysis. BMC Psychiatry, 21(1).",
    "url": "https://doi.org/10.1186/s12888-020-03017-w"
  },
  {
    "text": "Raikes, H., & Thompson, R. (2005). Efficacy and social support as predictors of parenting stress among families in poverty. Inf Mental Hlth J, 26, 177–190.",
    "url": null
  },
  {
    "text": "Raines, D. (2013). Mothers’ stressor as the day of discharge from the NICU approaches. Adv Neonatal Care, 13, 181–187.",
    "url": null
  },
  {
    "text": "Räisänen, S., Lehto, S. M., Nielsen, H. S., Gissler, M., Kramer, M. R., & Heinonen, S. (2013). Fear of childbirth predicts postpartum depression: A population-based analysis of 511 422 singleton births in Finland. BMJ Open, 3(11), e004047.",
    "url": "https://doi.org/10.1136/bmjopen-2013-004047"
  },
  {
    "text": "Ravaldi, C., Mosconi, L., Bonaiuti, R., & Vannacci, A. (2023). The Emotional Landscape of Pregnancy and Postpartum during the COVID-19 Pandemic in Italy: A Mixed-Method Analysis Using Artificial Intelligence. Journal of Clinical Medicine, 12(19), 6140.",
    "url": "https://doi.org/10.3390/jcm12196140"
  },
  {
    "text": "Redshaw, M., & Hamilton, K. (2010). Family centred care? Facilities, information and support for parents in UK neonatal units. Arch Dis Child Fetal Neonatal Ed, 95, F365–F368.",
    "url": null
  },
  {
    "text": "Rees, S., Channon, S., & Waters, C. S. (2019). The impact of maternal prenatal and postnatal anxiety on children’s emotional problems: A systematic review. European Child & Adolescent Psychiatry, 28(2), 257–280.",
    "url": "https://doi.org/10.1007/s00787-018-1173-5"
  },
  {
    "text": "Regus, P. (n.d.-a). Postpartum Depression: Standardizing Motherhood? [Georgia State University].",
    "url": "https://doi.org/10.57709/2764269"
  },
  {
    "text": "Regus, P. (n.d.-b). The Emerging Medicalization of Postpartum Depression: Tightening the Boundaries of Motherhood.",
    "url": "https://doi.org/10.57709/1062357"
  },
  {
    "text": "Reich, S., Bickman, L., & Heflinger, C. (2004). Covariates of self-efficacy: Caregiver characteristics related to mental health services self-efficacy. J Emot Behav Disord, 12, 99–108.",
    "url": null
  },
  {
    "text": "Reloj, J. P. (2024). Lived Experiences of Single Mothers After Childbirth: Postpartum Depression (PPD) Stories. Diversitas Journal, 9(1).",
    "url": "https://doi.org/10.48017/dj.v9i1.2926"
  },
  {
    "text": "Repič Slavič, T., & Gostečnik, C. (2015). The Experience of Pregnancy, Childbirth and Motherhood in Women with a History of Sexual Abuse. In A. Polona Mivšek (Ed.), Sexology in Midwifery. InTech.",
    "url": "https://doi.org/10.5772/59085"
  },
  {
    "text": "Ridyard, C., & Hughes, D. (2010). Methods for the collection of resource use data within clinical trials: A systematic review of trials funded by the UK Health Technology Assessment programme. Value Health, 13, 867–872.",
    "url": null
  },
  {
    "text": "Riem, M. M. E., Bakermans-Kranenburg, M. J., Cima, M., & Van IJzendoorn, M. H. (2023). Grandparental Support and Maternal Postpartum Mental Health: A Review and Meta-Analysis. Human Nature, 34(1), 25–45.",
    "url": "https://doi.org/10.1007/s12110-023-09440-8"
  },
  {
    "text": "Roa, E., & Ettenberger, M. (2018). Music Therapy Self-Care Group for Parents of Preterm Infants in the Neonatal Intensive Care Unit: A Clinical Pilot Intervention. Medicines, 5(4), 134.",
    "url": "https://doi.org/10.3390/medicines5040134"
  },
  {
    "text": "Rose, C., Ramsay, L., & Leaf, A. (2008). Strategies for getting preterm infants home earlier. Arch Dis Child, 93, 271–273.",
    "url": null
  },
  {
    "text": "Røseth, I., Bongaardt, R., Lyberg, A., Sommerseth, E., & Dahl, B. (2018). New mothers’ struggles to love their child. An interpretative synthesis of qualitative studies. International Journal of Qualitative Studies on Health and Well-Being, 13(1), 1490621.",
    "url": "https://doi.org/10.1080/17482631.2018.1490621"
  },
  {
    "text": "Rotkirch, A., & Janhunen, K. (2010). Maternal Guilt. Evolutionary Psychology, 8(1), 90–106.",
    "url": "https://doi.org/10.1177/147470491000800108"
  },
  {
    "text": "Russell, C. G., Taki, S., Azadi, L., Campbell, K. J., Laws, R., Elliott, R., & Denney-Wilson, E. (2016). A qualitative study of the infant feeding beliefs and behaviours of mothers with low educational attainment. BMC Pediatrics, 16(1), 69.",
    "url": "https://doi.org/10.1186/s12887-016-0601-2"
  },
  {
    "text": "Russell, P. S., Birtel, M. D., Smith, D. M., Hart, K., & Newman, R. (2021). Infant feeding and internalized stigma: The role of guilt and shame. Journal of Applied Social Psychology, 51(9), 906–919.",
    "url": "https://doi.org/10.1111/jasp.12810"
  },
  {
    "text": "Saady Mohamed Sayed, S., Mohamed El-Sayed El-Shabory, N. E.-H., Abdel–Fatah Mohamed Shehata, A., & Abdel-rahman Osman, S. (2024). Effect of Care Provided Using “Orem Self-Care Model” on Postpartum Dependent Care among Primiparous Mothers. Egyptian Journal of Health Care, 15(2), 1607–1623.",
    "url": "https://doi.org/10.21608/ejhc.2024.376406"
  },
  {
    "text": "Sadegh Heydarpoor, Mahla Yazarloo, Maryam Ebrahimpour Roodposhti, Fatemeh Ataei, Zahra Abdolreza Gharehbagh, Somayeh Azimpour, & Hamid Hojjati. (2022). The Effect of Spiritual Self-care Training on the Anxiety of Mothers of Premature Infants Admitted to NICUs. Journal of Pharmaceutical Negative Results, 13(7), 339–343.",
    "url": "https://doi.org/10.47750/pnr.2022.13.S07.047"
  },
  {
    "text": "Saeed, Q., Shafique, K., & Chaudhry, N. (2024). Lived experiences of mothers with postnatal anxiety: A qualitative phenomenology study from Pakistan. BMJ Open, 14(5), e078203.",
    "url": "https://doi.org/10.1136/bmjopen-2023-078203"
  },
  {
    "text": "Sahar Ahmad Shafik, Rasha Mohamed Hassan Eltelt, & Marwa Jabbar Saiwan. (2024). Empowering New Mothers Wellness: Navigating Postpartum Stress and Anxiety through Psycho-Educational Program. Obstetrics and Gynaecology Forum, 34(3), 2768–2780.",
    "url": null
  },
  {
    "text": "Saharoy, R., Potdukhe, A., Wanjari, M., & Taksande, A. B. (2023). Postpartum Depression and Maternal Care: Exploring the Complex Effects on Mothers and Infants. Cureus.",
    "url": "https://doi.org/10.7759/cureus.41381"
  },
  {
    "text": "Sakamoto, J. L., Carandang, R. R., Kharel, M., Shibanuma, A., Yarotskaya, E., Basargina, M., & Jimba, M. (2022). Effects of mHealth on the psychosocial health of pregnant women and mothers: A systematic review. BMJ Open, 12(2), e056807.",
    "url": "https://doi.org/10.1136/bmjopen-2021-056807"
  },
  {
    "text": "Samya Shinde. (2018b). Postpartum Depression and the Social Construction of Motherhood: A Gendered Perspective. Indian Journal of Mental Health, 5(1), 42–47.",
    "url": ""
  },
  {
    "text": "Sayed, S., Khamis, M., & Arief, A. (2022). Improvement of Self-care of Postpartum Minor Discomfort. Assiut Scientific Nursing Journal, 10(32), 126–137.",
    "url": "https://doi.org/10.21608/asnj.2022.155739.1416"
  },
  {
    "text": "Seo, J.-M., Kim, S.-J., Na, H., Kim, J.-H., & Lee, H. (2022). Effectiveness of a Mobile Application for Postpartum Depression Self-Management: Evidence from a Randomised Controlled Trial in South Korea. Healthcare, 10(11), 2185.",
    "url": "https://doi.org/10.3390/healthcare10112185"
  },
  {
    "text": "Shalaby Salama, E., Helmy, H., Elsadek Mohammed, A., & Mahmoud, O. (2022). Effect of Nursing Intervention Program for Women post-Caesarean Section regarding Self-Care on their Psychological status and Postoperative Pain. Assiut Scientific Nursing Journal, 10(29), 112–126.",
    "url": "https://doi.org/10.21608/asnj.2022.124954.1331"
  },
  {
    "text": "Shamsa Hadi, H. H. (2015). Uncovering the Concealed Part of Motherhood-Postpartum Depression in Mothers. Clinics in Mother and Child Health, 12(1).",
    "url": "https://doi.org/10.4172/2090-7214.1000169"
  },
  {
    "text": "Sharifipour, F., Javadnoori, M., Behboodi Moghadam, Z., Najafian, M., Cheraghian, B., & Abbaspoor, Z. (2022). Interventions to improve social support among postpartum mothers: A systematic review. Health Promotion Perspectives, 12(2), 141–150.",
    "url": "https://doi.org/10.34172/hpp.2022.18"
  },
  {
    "text": "Shepherd, L., Walbey, C., & Lovell, B. (2017). The Role of Social-Cognitive and Emotional Factors on Exclusive Breastfeeding Duration. Journal of Human Lactation, 33(3), 606–613.",
    "url": "https://doi.org/10.1177/0890334417708187"
  },
  {
    "text": "Slomian, J., Emonts, P., Vigneron, L., Acconcia, A., Glowacz, F., Reginster, J. Y., Oumourgh, M., & Bruyère, O. (2017). Identifying maternal needs following childbirth: A qualitative study among mothers, fathers and professionals. BMC Pregnancy and Childbirth, 17(1), 213.",
    "url": "https://doi.org/10.1186/s12884-017-1398-1"
  },
  {
    "text": "Slomian, J., Honvo, G., Emonts, P., Reginster, J.-Y., & Bruyère, O. (2019). Consequences of maternal postpartum depression: A systematic review of maternal and infant outcomes. Women’s Health, 15, 1745506519844044.",
    "url": "https://doi.org/10.1177/1745506519844044"
  },
  {
    "text": "Smith, V., Young, S., Pursley, D., McCormick, M., & Zupancic, J. (2009). Are families prepared for discharge from the NICU? J Perinatol, 29, 623–629.",
    "url": ""
  },
  {
    "text": "Sommerfeldt, B., Skårderud, F., Kvalem, I. L., Gulliksen, K. S., & Holte, A. (2023). IVF-induced pregnancy and early motherhood among women with a history of severe eating disorders. Frontiers in Psychology, 14, 1126941.",
    "url": "https://doi.org/10.3389/fpsyg.2023.1126941"
  },
  {
    "text": "Sorsa, M. A., Kylmä, J., & Bondas, T. E. (2021). Contemplating Help-Seeking in Perinatal Psychological Distress—A Meta-Ethnography. International Journal of Environmental Research and Public Health, 18(10), 5226.",
    "url": "https://doi.org/10.3390/ijerph18105226"
  },
  {
    "text": "Staehelin, K., Kurth, E., Schindler, C., Schmid, M., & Zemp, S. (2013). Predictors of early postpartum mental distress in mothers with midwifery home care – results from a nested case-control study. Swiss Medical Weekly.",
    "url": "https://doi.org/10.4414/smw.2013.13862"
  },
  {
    "text": "Stamou, G., García-Palacios, A., & Botella, C. (2018). Cognitive-Behavioural therapy and interpersonal psychotherapy for the treatment of post-natal depression: A narrative review. BMC Psychology, 6(1), 28.",
    "url": "https://doi.org/10.1186/s40359-018-0240-5"
  },
  {
    "text": "Stojanov, J., Stankovic, M., Zikic, O., Stankovic, M., & Stojanov, A. (2021). The risk for nonpsychotic postpartum mood and anxiety disorders during the COVID-19 pandemic. The International Journal of Psychiatry in Medicine, 56(4), 228–239.",
    "url": "https://doi.org/10.1177/0091217420981533"
  },
  {
    "text": "Suzuki, S., & Eto, M. (2017). Screening for Depressive and Anxiety Symptoms During Pregnancy and Postpartum at a Japanese Perinatal Center. Journal of Clinical Medicine Research, 9(6), 512–515.",
    "url": "https://doi.org/10.14740/jocmr3035w"
  },
  {
    "text": "Taalab, A., Qasem, E., Gamal, A., & Ashour, S. (2021). Relationship between Postpartum Primiparous Sleep Quality and Self-Care. Menoufia Nursing Journal, 6(2), 137–154.",
    "url": "https://doi.org/10.21608/menj.2021.225858"
  },
  {
    "text": "Takács, L., Smolík, F., & Putnam, S. (2019). Assessing longitudinal pathways between maternal depressive symptoms, parenting self-esteem and infant temperament. PLOS ONE, 14(8), e0220633.",
    "url": "https://doi.org/10.1371/journal.pone.0220633"
  },
  {
    "text": "Teeraporn Boonyaprapapan, & Prangthip Thasanoh Elter. (2021). Factors contributing to self-care behavior of early postpartum mothers in rural Nakhon Ratchasima, Thailand. Suranaree Journal of Science and Technology, 28(1), 070015(1-9).",
    "url": ""
  },
  {
    "text": "Tembo, C., Portsmouth, L., & Burns, S. (2024). Cultural and social factors associated with generalized anxiety disorder among adolescent mothers during the postpartum period in Malawi: A cross-sectional survey. BMC Global and Public Health, 2(1).",
    "url": "https://doi.org/10.1186/s44263-024-00080-3"
  },
  {
    "text": "Teti, D., & Gelfand, D. (1991). Behavioural competence among mothers of infants in the first year: The mediational role of maternal self-efficacy. Child Dev, 62, 918–929.",
    "url": ""
  },
  {
    "text": "Thorstensson, S., Hertfelt Wahn, E., Ekström, A., & Langius-Eklöf, A. (2012). Evaluation of the Mother-to-Infant relation and feeling scale: Interviews with first-time mothers’ for feelings and relation to their baby three days after birth. International Journal of Nursing and Midwifery, 4(1), 8–15.",
    "url": "https://doi.org/10.5897/IJNM11.041"
  },
  {
    "text": "Tichelman, E., Westerneng, M., Witteveen, A. B., Van Baar, A. L., Van Der Horst, H. E., De Jonge, A., Berger, M. Y., Schellevis, F. G., Burger, H., & Peters, L. L. (2019). Correlates of prenatal and postnatal mother-to-infant bonding quality: A systematic review. PLOS ONE, 14(9), e0222998.",
    "url": "https://doi.org/10.1371/journal.pone.0222998"
  },
  {
    "text": "Tirumalaraju, V., Suchting, R., Evans, J., Goetzl, L., Refuerzo, J., Neumann, A., Anand, D., Ravikumar, R., Green, C. E., Cowen, P. J., & Selvaraj, S. (2020). Risk of Depression in the Adolescent and Adult Offspring of Mothers With Perinatal Depression: A Systematic Review and Meta-analysis. JAMA Network Open, 3(6), e208783.",
    "url": "https://doi.org/10.1001/jamanetworkopen.2020.8783"
  },
  {
    "text": "Tokumitsu, K., Sugawara, N., Maruo, K., Suzuki, T., Shimoda, K., & Yasui-Furukori, N. (2020). Prevalence of perinatal depression among Japanese women: A meta-analysis. Annals of General Psychiatry, 19(1), 41.",
    "url": "https://doi.org/10.1186/s12991-020-00290-7"
  },
  {
    "text": "Trumello, C., Candelori, C., Cofini, M., Cimino, S., Cerniglia, L., Paciello, M., & Babore, A. (2018). Mothers’ Depression, Anxiety, and Mental Representations After Preterm Birth: A Study During the Infant’s Hospitalization in a Neonatal Intensive Care Unit. Frontiers in Public Health, 6.",
    "url": "https://doi.org/10.3389/fpubh.2018.00359"
  },
  {
    "text": "Umylny, P., German, M., & Lantiere, A. (2017). Treating postpartum mood and anxiety disorders in primary care pediatrics. Current Problems in Pediatric and Adolescent Health Care, 47(10), 254–266.",
    "url": "https://doi.org/10.1016/j.cppeds.2017.08.003"
  },
  {
    "text": "Upadhyay, R. P., Chowdhury, R., Aslyeh Salehi, Sarkar, K., Singh, S. K., Sinha, B., Pawar, A., Rajalakshmi, A. K., & Kumar, A. (2017). Postpartum depression in India: A systematic review and meta-analysis. Bulletin of the World Health Organization, 95(10), 706-717C.",
    "url": "https://doi.org/10.2471/BLT.17.192237"
  },
  {
    "text": "Valizadeh, S., Hosseinzadeh, M., Mohammadi, E., Hassankhani, H., Fooladi, M. M., & Cummins, A. (2018). Coping mechanism against high levels of daily stress by working breastfeeding mothers in Iran. International Journal of Nursing Sciences, 5(1), 39–44.",
    "url": "https://doi.org/10.1016/j.ijnss.2017.12.005"
  },
  {
    "text": "Van Den Heuvel, J. F., Groenhof, T. K., Veerbeek, J. H., Van Solinge, W. W., Lely, A. T., Franx, A., & Bekker, M. N. (2018). eHealth as the Next-Generation Perinatal Care: An Overview of the Literature. Journal of Medical Internet Research, 20(6), e202.",
    "url": "https://doi.org/10.2196/jmir.9262"
  },
  {
    "text": "Van Der Pal, S., Maguire, C., Le Cessie, S., Wit, J., Walther, F., & Bruil, J. (2007). Parental experiences during the first period at the neonatal unit after two developmental care interventions. Acta Paediatr, 96, 1611–1616.",
    "url": ""
  },
  {
    "text": "Van Lieshout, R. J., Layton, H., Savoy, C. D., Xie, F., Brown, J. S. L., Huh, K., Bieling, P. J., Streiner, D. L., Ferro, M. A., & Haber-Evans, E. (2023). In-person 1-day cognitive behavioral therapy-based workshops for postpartum depression: A randomized controlled trial. Psychological Medicine, 53(14), 6888–6898.",
    "url": "https://doi.org/10.1017/S0033291723000454"
  },
  {
    "text": "Vazquez, V., & Cong, X. (2014). Parenting the NICU infant: A meta-ethnographic synthesis. International Journal of Nursing Sciences, 1(3), 281–290.",
    "url": "https://doi.org/10.1016/j.ijnss.2014.06.001"
  },
  {
    "text": "Veisani, Y., Delpisheh, A., Sayehmiri, K., & Rezaeian, S. (2013). Trends of Postpartum Depression in Iran: A Systematic Review and Meta-Analysis. Depression Research and Treatment, 2013, 1–8.",
    "url": "https://doi.org/10.1155/2013/291029"
  },
  {
    "text": "Veringa, I. K., De Bruin, E. I., Bardacke, N., Duncan, L. G., Van Steensel, F. J. A., Dirksen, C. D., & Bögels, S. M. (2016). ‘I’ve Changed My Mind’, Mindfulness-Based Childbirth and Parenting (MBCP) for pregnant women with a high level of fear of childbirth and their partners: Study protocol of the quasi-experimental controlled trial. BMC Psychiatry, 16(1), 377.",
    "url": "https://doi.org/10.1186/s12888-016-1070-8"
  },
  {
    "text": "Vik, K., & Hafting, M. (2012). “Smile through It!” Keeping up the Facade While Suffering from Postnatal Depressive Symptoms and Feelings of Loss: Findings of a Qualitative Study. Psychology, 03(09), 810–817.",
    "url": "https://doi.org/10.4236/psych.2012.329123"
  },
  {
    "text": "Vismara, L., Rollè, L., Agostini, F., Sechi, C., Fenaroli, V., Molgora, S., Neri, E., Prino, L. E., Odorisio, F., Trovato, A., Polizzi, C., Brustia, P., Lucarelli, L., Monti, F., Saita, E., & Tambelli, R. (2016). Perinatal Parenting Stress, Anxiety, and Depression Outcomes in First-Time Mothers and Fathers: A 3- to 6-Months Postpartum Follow-Up Study. Frontiers in Psychology, 7.",
    "url": "https://doi.org/10.3389/fpsyg.2016.00938"
  },
  {
    "text": "Wallace, K., & Araji, S. (2020). An Overview of Maternal Anxiety During Pregnancy and the Post-Partum Period. Journal of Mental Health & Clinical Psychology, 4(4), 47–56.",
    "url": "https://doi.org/10.29245/2578-2959/2020/4.1221"
  },
  {
    "text": "Wang, D., Li, Y.-L., Qiu, D., & Xiao, S.-Y. (2021). Factors Influencing Paternal Postpartum Depression: A Systematic Review and Meta-Analysis. Journal of Affective Disorders, 293, 51–63.",
    "url": "https://doi.org/10.1016/j.jad.2021.05.088"
  },
  {
    "text": "Wang, Z., Liu, J., Shuai, H., Cai, Z., Fu, X., Liu, Y., Xiao, X., Zhang, W., Krabbendam, E., Liu, S., Liu, Z., Li, Z., & Yang, B. X. (2021). Mapping global prevalence of depression among postpartum women. Translational Psychiatry, 11(1).",
    "url": "https://doi.org/10.1038/s41398-021-01663-6"
  },
  {
    "text": "Werchan, D. M., Hendrix, C. L., Ablow, J. C., Amstadter, A. B., Austin, A. C., Babineau, V., Anne Bogat, G., Cioffredi, L.-A., Conradt, E., Crowell, S. E., Dumitriu, D., Fifer, W., Firestein, M. R., Gao, W., Gotlib, I. H., Graham, A. M., Gregory, K. D., Gustafsson, H. C., Havens, K. L., … Brito, N. H. (2022). Behavioral coping phenotypes and associated psychosocial outcomes of pregnant and postpartum women during the COVID-19 pandemic. Scientific Reports, 12(1), 1209.",
    "url": "https://doi.org/10.1038/s41598-022-05299-4"
  },
  {
    "text": "Wielenga, J., Smith, B., & Unk, L. (2006). How satisfied are parents supported by nurses with the NIDCAP model of care for their preterm infant? J Nurs Care Qual, 21, 41–48.",
    "url": ""
  },
  {
    "text": "Williams, B. M., Christopher, K., & Sinski, J. (2017). “Who Doesn’t Want to Be This Hot Mom?”: Celebrity Mom Profiles and Mothers’ Accounts of Their Postpartum Bodies. Sage Open, 7(3), 2158244017720562.",
    "url": "https://doi.org/10.1177/2158244017720562"
  },
  {
    "text": "Wittkowski, A., Garrett, C., Cooper, A., & Wieck, A. (2017). The Relationship Between Postpartum Depression And Cognitions About Motherhood During Pregnancy. Journal of Woman’s Reproductive Health, 1(4), 9–23.",
    "url": "https://doi.org/10.14302/issn.2381-862X.jwrh-15-848"
  },
  {
    "text": "Woldeyohannes, D., Tekalegn, Y., Sahiledengle, B., Ermias, D., Ejajo, T., & Mwanri, L. (2021). Effect of postpartum depression on exclusive breast-feeding practices in sub-Saharan Africa countries: A systematic review and meta-analysis. BMC Pregnancy and Childbirth, 21(1), 113.",
    "url": "https://doi.org/10.1186/s12884-020-03535-1"
  },
  {
    "text": "Worrall, S., Harris, E. J., Silverio, S. A., & Fallon, V. (2023). The identification and measurement of postpartum anxiety in England: A Delphi survey. Journal of Psychiatric Research, 168, 381–391.",
    "url": "https://doi.org/10.1016/j.jpsychires.2023.10.041"
  },
  {
    "text": "Wyszynski, D. F., Hernandez-Diaz, S., Gordon-Dseagu, V., Ramiro, N., Basu, A., Kim, H. H., & Koenen, K. C. (2021). Frequency and source of worries in an International sample of pregnant and postpartum women during the Covid-19 pandemic. BMC Pregnancy and Childbirth, 21(1).",
    "url": "https://doi.org/10.1186/s12884-021-04241-2"
  },
  {
    "text": "Xavier, S., Branquinho, M., Pires, R., Moreira, H., Coelho, M., & Araújo-Pedrosa, A. (2023). Dysfunctional Attitudes Toward Motherhood and Depressive Symptoms in Portuguese Pregnant Women During COVID-19 Pandemic: The Mediating Roles of Self-Compassion and Mindful Self-Care. Mindfulness, 14(2), 418–428.",
    "url": "https://doi.org/10.1007/s12671-022-02049-5"
  },
  {
    "text": "Xiao, X., Loke, A. Y., Zhu, S., Gong, L., Shi, H., & Ngai, F. (2020). “The sweet and the bitter”: Mothers’ experiences of breastfeeding in the early postpartum period: a qualitative exploratory study in China. International Breastfeeding Journal, 15(1), 12.",
    "url": "https://doi.org/10.1186/s13006-020-00256-1"
  },
  {
    "text": "Yelland, J., Sutherland, G., & Brown, S. J. (2010). Postpartum anxiety, depression and social health: Findings from a population-based survey of Australian women. BMC Public Health, 10(1).",
    "url": "https://doi.org/10.1186/1471-2458-10-771"
  },
  {
    "text": "Yonkers, K. A., Ramin, S. M., Rush, A. J., Navarrete, C. A., Carmody, T., March, D., Heartwell, S. F., & Leveno, K. J. (2001). Onset and Persistence of Postpartum Depression in an Inner-City Maternal Health Clinic System. American Journal of Psychiatry, 158(11), 1856–1863.",
    "url": "https://doi.org/10.1176/appi.ajp.158.11.1856"
  },
  {
    "text": "Youseflu, S., Kohan, S., & Mostafavi, F. (2023). Promoting adolescent mother self-efficacy for parenting roles, and self-care after childbirth: Protocol for a mixed methods study. Reproductive Health, 20(1), 146.",
    "url": "https://doi.org/10.1186/s12978-023-01679-9"
  },
  {
    "text": "Yusefi Avrand, M., Davoodabadi-Farahani, M., & Nouruzi, E. (2022). The effect of group counseling based on emotional intelligence in the prevention of postpartum depression. Journal of Nursing and Midwifery Sciences, 9(2), 90.",
    "url": "https://doi.org/10.4103/JNMS.JNMS_60_20"
  },
  {
    "text": "Zacher Kjeldsen, M.-M., Bricca, A., Liu, X., Frokjaer, V. G., Madsen, K. B., & Munk-Olsen, T. (2022). Family History of Psychiatric Disorders as a Risk Factor for Maternal Postpartum Depression: A Systematic Review and Meta-analysis. JAMA Psychiatry, 79(10), 1004.",
    "url": "https://doi.org/10.1001/jamapsychiatry.2022.2400"
  },
  {
    "text": "Zamanzadeh, V., Namnabati, M., Valizadeh, L., & Badiee, Z. (2013). Mothers’ experiences of infants discharge in Iranian NICU culture: A qualitative study. Adv Neonatal Care, 13, E1–E7.",
    "url": ""
  },
  {
    "text": "Zengin, H., Bafali, I., Caka, S., Tiryaki, O., & Cinar, N. (2020). Childbirth and Postpartum Period Fear and the Related Factors in Pregnancy. Journal of the College of Physicians and Surgeons Pakistan, 30(02), 144–148.",
    "url": "https://doi.org/10.29271/jcpsp.2020.02.144"
  },
  {
    "text": "Zhang, Q., Dai, X., & Li, W. (2022). Comparative efficacy and acceptability of pharmacotherapies for postpartum depression: A systematic review and network meta-analysis. Frontiers in Pharmacology, 13, 950004.",
    "url": "https://doi.org/10.3389/fphar.2022.950004"
  },
  {
    "text": "Zhang, S., Wang, L., Yang, T., Chen, L., Qiu, X., Wang, T., Chen, L., Zhao, L., Ye, Z., Zheng, Z., & Qin, J. (2019). Maternal violence experiences and risk of postpartum depression: A meta-analysis of cohort studies. European Psychiatry, 55, 90–101.",
    "url": "https://doi.org/10.1016/j.eurpsy.2018.10.005"
  },
  {
    "text": "Zhang, Y., Zhu, J., Li, S., Huang, L., Fang, Q., & Zheng, X. (2023). The effectiveness of an internet-based support program on maternal self-efficacy, postpartum depression and social support for primiparous women during the COVID-19 pandemic: Randomized controlled trial. Frontiers in Public Health, 11, 1035872.",
    "url": "https://doi.org/10.3389/fpubh.2023.1035872"
  },
  {
    "text": "Zhao, L., Chen, J., Lan, L., Deng, N., Liao, Y., Yue, L., Chen, I., Wen, S. W., & Xie, R. (2021). Effectiveness of Telehealth Interventions for Women With Postpartum Depression: Systematic Review and Meta-analysis. JMIR mHealth and uHealth, 9(10), e32544.",
    "url": "https://doi.org/10.2196/32544"
  },
  {
    "text": "Zietlow, A.-L., Nonnenmacher, N., Reck, C., Ditzen, B., & Müller, M. (2019). Emotional Stress During Pregnancy – Associations With Maternal Anxiety Disorders, Infant Cortisol Reactivity, and Mother–Child Interaction at Pre-school Age. Frontiers in Psychology, 10.",
    "url": "https://doi.org/10.3389/fpsyg.2019.02179"
  }
  ]);

// Search functionality
const searchQuery = ref('');

// Pagination logic
const itemsPerPage = 10;
const currentPage = ref(1);

// Filter references based on search query
const filteredReferences = computed(() => {
  if (!searchQuery.value.trim()) {
    return references.value;
  }
  
  const query = searchQuery.value.toLowerCase().trim();
  return references.value.filter(reference => 
    reference.text.toLowerCase().includes(query)
  );
});

const totalReferences = computed(() => filteredReferences.value.length);
const totalPages = computed(() => Math.ceil(totalReferences.value / itemsPerPage));

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage);
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage, totalReferences.value));

const paginatedReferences = computed(() => {
  return filteredReferences.value.slice(startIndex.value, endIndex.value);
});

const visiblePages = computed(() => {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    // Scroll to top of references
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

// Search functions
const onSearchInput = () => {
  // Reset to first page when searching
  currentPage.value = 1;
};

const clearSearch = () => {
  searchQuery.value = '';
  currentPage.value = 1;
};
  
  // Note: You would need to add all the remaining references following the same pattern
  // Each reference should be an object with 'text' and 'url' properties
  </script>
  
  <style lang="scss" scoped>
  @import "~@/assets/scss/fonts.scss";
  
  .references-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
  }
  
  h1 {
    color: $color-pink-dark-2;
    text-align: center;
    margin-bottom: 15px;
    font-family: $font-regular;
    font-size: 28px;
  }

  .research-subtitle {
    text-align: center;
    color: #666;
    font-size: 16px;
    margin-bottom: 30px;
    font-family: $font-regular;
    line-height: 1.5;
  }
  
  .references-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .reference-item {
    background-color: #f9f9f9;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .reference-item p {
    font-size: 14px;
    margin-bottom: 15px;
    line-height: 1.5;
  }
  
  .link-button {
    display: inline-block;
    background-color: $color-pink-dark-2;
    color: white;
    padding: 8px 16px;
    border-radius: 4px;
    text-decoration: none;
    font-weight: 500;
    transition: background-color 0.3s ease;
    
    &:hover {
      background-color: darken($color-pink-dark-2, 10%);
    }
  }
  
  // Search styles
  .search-container {
    margin-bottom: 30px;
  }

  .search-input-wrapper {
    position: relative;
    max-width: 500px;
    margin: 0 auto 15px;
  }

  .search-input {
    width: 100%;
    padding: 12px 45px 12px 15px;
    border: 2px solid #ddd;
    border-radius: 25px;
    font-size: 16px;
    outline: none;
    transition: border-color 0.3s ease;

    &:focus {
      border-color: $color-pink-dark-2;
    }

    &::placeholder {
      color: #999;
    }
  }

  .search-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    color: #999;
    pointer-events: none;
  }

  .search-results-info {
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;

    p {
      color: #666;
      font-size: 14px;
      margin: 0;
    }
  }

  .clear-search-btn {
    background-color: #f5f5f5;
    color: #666;
    border: 1px solid #ddd;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.3s ease;

    &:hover {
      background-color: #e9e9e9;
      color: #333;
    }
  }

  // Pagination styles
  .pagination-info {
    text-align: center;
    margin-bottom: 20px;
    color: #666;
    font-size: 14px;
  }

  .pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 30px;
    flex-wrap: wrap;
  }

  .pagination-btn {
    background-color: $color-pink-dark-2;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s ease;

    &:hover:not(:disabled) {
      background-color: darken($color-pink-dark-2, 10%);
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }

  .pagination-numbers {
    display: flex;
    gap: 5px;
    align-items: center;
  }

  .pagination-number {
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;
    min-width: 40px;

    &:hover {
      background-color: #e9e9e9;
    }

    &.active {
      background-color: $color-pink-dark-2;
      color: white;
      border-color: $color-pink-dark-2;
    }
  }
  
  // Responsive design
  @media (max-width: 768px) {
    .references-container {
      padding: 15px;
    }
    
    .reference-item {
      padding: 15px;
    }

    .research-subtitle {
      font-size: 14px;
      margin-bottom: 20px;
    }

    .search-input {
      font-size: 14px;
      padding: 10px 40px 10px 12px;
    }

    .search-icon {
      right: 12px;
      font-size: 16px;
    }

    .search-results-info {
      flex-direction: column;
      gap: 10px;
    }

    .pagination-controls {
      gap: 5px;
    }

    .pagination-number {
      padding: 6px 10px;
      min-width: 35px;
      font-size: 12px;
    }

    .pagination-btn {
      padding: 6px 12px;
      font-size: 12px;
    }
  }
  </style>